import { useClassifyProfileUrls, useCreateCreatorBulkUpload } from '@round/creatorbase-helpers/api-hooks';
import { Accordion, Button, Empty, lucideIcons, Skeleton, toast } from '@round/creatorbase-ui';
import { FormikProps, FormikProvider, useFormik } from 'formik';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import {
    ClassifiedLineGroups,
    groupClassifiedLines,
    NewCreatorsFormState,
    newCreatorsValidationSchema,
    toCreatorBulkUploadRow,
    toNewCreatorRows,
} from '../../helpers';
import { useCreatorAccountRows } from '../../hooks/useCreatorAccountRows';
import ClassifiedSection from '../ClassifiedSection/ClassifiedSection';
import CreatorAccountsTable from '../CreatorAccountsTable/CreatorAccountsTable';
import { CreatorAccountRow } from '../CreatorAccountsTable/helpers';
import ErrorsTable from './components/ErrorsTable/ErrorsTable';
import NewCreatorsTable from './components/NewCreatorsTable/NewCreatorsTable';
import { toExistingCreatorAccounts } from './helpers';
import styles from './ReviewCreators.module.css';

const { AlertTriangleIcon } = lucideIcons;

const NEW_SECTION = 'new';
const SKELETON_SECTION_COUNT = 3;

const getAddCreatorsLabel = (count: number) => {
    if (count === 0) {
        return 'Add creators';
    }

    return `Add ${count} ${count === 1 ? 'creator' : 'creators'}`;
};

type Props = {
    urls: string[];
    onCreated: (uploadId: number) => void;
};

const ReviewCreators = ({ urls, onCreated }: Props) => {
    const { selectedClientId } = useClientSelect();
    // Fetched once: a refetch that came back with different lines would reseed the form and drop
    // the user's edits.
    const { results, isLoading, isError, isSuccess, errorMessage } = useClassifyProfileUrls(
        { client_id: selectedClientId, lines: urls },
        { staleTime: Infinity }
    );

    const { create, isLoading: isCreating, getErrorMessage } = useCreateCreatorBulkUpload();

    const groups = groupClassifiedLines(results);
    const existing = useCreatorAccountRows(toExistingCreatorAccounts(groups.EXISTS));
    // The upload is always made for the selected client rather than one the backend infers.
    const isClientSelected = typeof selectedClientId === 'number';
    const canAddCreators = isSuccess && isClientSelected && groups.NEW.length > 0;

    // Classify resolves after mount, so the rows have to be seeded once it does.
    const formik = useFormik<NewCreatorsFormState>({
        initialValues: { rows: toNewCreatorRows(groups.NEW) },
        enableReinitialize: true,
        validationSchema: newCreatorsValidationSchema,
        onSubmit: async (values) => {
            if (!isClientSelected) {
                return;
            }

            try {
                const { upload_id } = await create({
                    client_id: selectedClientId,
                    rows: values.rows.map(toCreatorBulkUploadRow),
                });
                onCreated(upload_id);
            } catch (error) {
                toast.error(error instanceof Error ? getErrorMessage(error) : 'Could not add creators');
            }
        },
    });

    return (
        <>
            <ReviewCreatorsBody
                groups={groups}
                existingRows={existing.rows}
                isExploreLoading={existing.isExploreLoading}
                isImagesLoading={existing.isImagesLoading}
                formik={formik}
                isLoading={isLoading}
                isError={isError}
                errorMessage={errorMessage}
            />

            <div className={styles.footer}>
                <Button
                    size="sm"
                    variant="default"
                    disabled={!canAddCreators || isCreating}
                    isLoading={isCreating}
                    onClick={formik.handleSubmit}
                >
                    {getAddCreatorsLabel(groups.NEW.length)}
                </Button>
            </div>
        </>
    );
};

export default ReviewCreators;

type BodyProps = {
    groups: ClassifiedLineGroups;
    /** The accounts the existing lines matched, with what Creator Explore has on them. */
    existingRows: CreatorAccountRow[];
    isExploreLoading: boolean;
    isImagesLoading: boolean;
    formik: FormikProps<NewCreatorsFormState>;
    isLoading: boolean;
    isError: boolean;
    errorMessage: string | null;
};

const ReviewCreatorsBody = ({
    groups,
    existingRows,
    isExploreLoading,
    isImagesLoading,
    formik,
    isLoading,
    isError,
    errorMessage,
}: BodyProps) => {
    if (isError) {
        return (
            <Empty>
                <Empty.Media variant="icon">
                    <AlertTriangleIcon />
                </Empty.Media>
                <Empty.Title>Something went wrong</Empty.Title>
                <Empty.Description>{errorMessage || 'These URLs could not be checked.'}</Empty.Description>
            </Empty>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.skeletons}>
                {Array.from({ length: SKELETON_SECTION_COUNT }).map((_, index) => (
                    <Skeleton key={index} className={styles.sectionSkeleton} />
                ))}
            </div>
        );
    }

    return (
        <Accordion type="multiple" defaultValue={[NEW_SECTION]} className={styles.sections}>
            {groups.UNPARSEABLE.length > 0 && (
                <ClassifiedSection value="errors" title="Errors" count={groups.UNPARSEABLE.length}>
                    <ErrorsTable lines={groups.UNPARSEABLE} />
                </ClassifiedSection>
            )}

            {existingRows.length > 0 && (
                <ClassifiedSection value="existing" title="Existing creators" count={existingRows.length}>
                    <CreatorAccountsTable
                        rows={existingRows}
                        isExploreLoading={isExploreLoading}
                        isImagesLoading={isImagesLoading}
                    />
                </ClassifiedSection>
            )}

            {groups.NEW.length > 0 && (
                <ClassifiedSection value={NEW_SECTION} title="New creators" count={groups.NEW.length}>
                    <FormikProvider value={formik}>
                        <NewCreatorsTable />
                    </FormikProvider>
                </ClassifiedSection>
            )}
        </Accordion>
    );
};
